import { Observable } from 'rxjs';
import { SignalChange } from './signal.types';

/**
 * Abstraction over the real-time signal transport.
 * Implementations: FirebaseSignalProvider (now), SupabaseSignalProvider (future).
 *
 * The abstract class doubles as the Angular DI token. To swap providers, change
 * one line in the app providers:
 *   { provide: SignalProvider, useClass: FirebaseSignalProvider }
 * becomes:
 *   { provide: SignalProvider, useClass: SupabaseSignalProvider }
 *
 * Components and the SignalCoordinator depend only on this abstract type.
 */
export abstract class SignalProvider {
  /**
   * Set the environment namespace every signal path is prefixed with.
   *
   * Supplied by the SERVER (auth payload `signal_namespace`), not the frontend build, so a
   * build pointed at a different API can never listen on the wrong environment's node.
   * Call before connect()/watchValues().
   */
  abstract setNamespace(namespace: string | null | undefined): void;

  /** Connect a listener for a family-root scope. */
  abstract connect(familyRootId: number, scope: string): void;

  /** Observable of signal changes (emits only the keys that changed). */
  abstract get changes$(): Observable<SignalChange>;

  /**
   * Watch a scope's raw values rather than its changed keys.
   *
   * Most signal nodes hold bare timestamps and callers only need to know *that* something
   * changed. A few nodes (currently drawing progress) carry a payload per key, and this
   * returns that payload directly so no re-fetch is needed. The observable is shared and
   * ref-counted: the underlying listener is torn down when the last subscriber leaves.
   */
  abstract watchValues<T = any>(familyRootId: number, scope: string): Observable<Record<string, T>>;

  /** Disconnect a specific scope. */
  abstract disconnect(scope: string): void;

  /** Disconnect everything (e.g. on logout / office switch). */
  abstract disconnectAll(): void;
}
